import { useQuery } from "@tanstack/react-query";
import { Currency, PropertyType } from "src/types";
import axios from "axios";
import { findPropertiesByType } from "../components/constants/mockProperties";
import { MemberType } from "src/types";


export const getClientDomain = (message?: string): string => {
  if (typeof window === "undefined") {
    return "";
  }
  try {
    if (typeof window === "undefined") {
      throw new Error(message || "Window is undefined");
    }
    const { hostname } = window.location;

    // You can return the hostname directly
    return hostname;

    // Or use a mapping if you want a specific URL per hostname
    // const domainMap: Record<string, string> = {
    //   "olalekansalamiassociates.ourlisting.ng": "olalekansalamiassociates.ourlisting.ng",
    //   "taiwosalam.ourlisting.ng": "taiwosalam.ourlisting.ng",
    //   // add other mappings here
    // };
    // return domainMap[hostname] || "default.ourlisting.ng";
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.log({ error });
    console.error("Error getting client domain:", error.message);
    // fallback domain in case of error
    return window.location.hostname;
  }
};

const clientDomain = getClientDomain();
const isDemoMode = process.env.NEXT_PUBLIC_DEMO_MODE === "true";
const generateAddress = (property: Unit): string => {
  const unit_preference = property.unit_preference || "";
  const unit_type =
    property.unit_type === "industrial/commercial"
      ? "commercial"
      : property.unit_type || "Hello there";
  const number = property.number_of || "";
  const unit_sub_type = property.unit_sub_type || "";
  const bedroom = property.bedroom === "0" ? "" : `${property.bedroom} Bedroom`;
  // const bedroom = `${property.bedroom} Bedroom` || "";
  const total_area_sqm = property.total_area_sqm || "";
  const mappings: Record<string, string[]> = {
    residential: [unit_preference, bedroom, unit_sub_type, unit_type],
    industrial: [unit_preference, number, unit_sub_type],
    commercial: [unit_preference, total_area_sqm, unit_sub_type],
    land: [unit_preference, total_area_sqm, number, unit_sub_type],
    factory: [unit_preference, number, unit_sub_type],
    apartment: [unit_preference, bedroom, unit_sub_type, unit_type],
    "block of flat": [unit_preference, bedroom, unit_sub_type, unit_type],
    house: [unit_preference, bedroom, unit_sub_type, unit_type],
  };

  console.log("unit_type", unit_type);

  // Remove values that are "0" or "none"
  const filteredValues =
    mappings[unit_type]?.filter(
      (value) => value !== "0" && value.toLowerCase() !== "none"
    ) || [];

  // Capitalize the first letter of each word
  const capitalize = (str: string) =>
    str.replace(/\b\w/g, (char) => char.toUpperCase());
  const result = capitalize(filteredValues.join(" ").trim());
  console.log("result", result);
  return result;
};

export const transformUnit = (unit: Unit): PropertyType => {
  if (!unit) return {} as unknown as PropertyType;
  return {
    id: unit?.id ?? 0,
    vat_amount: unit?.vat_amount || "0",
    renew_vat_amount: unit?.renew_vat_amount,
    management_fee: unit.renew_agency_fee,
    security_fee: unit.renew_security_fee,
    coordinate: unit.property?.coordinate
      ? parseCoordinates(unit.property?.coordinate || "0,0")
      : (null as unknown as [number, number]),
    propertyId: unit?.id ?? 0,
    totalViews: unit.total_views,
    images: unit.images.map((image: UnitImage) => image.path || ""),
    beforePrice: undefined,
    availableUnits: unit.total_available_unit,
    total_vacant: unit.total_available_unit,
    total_units: unit.total_unit,
    vat:
      Array.isArray(unit?.rent_vat) && unit.rent_vat.length > 0
        ? unit?.rent_vat[0].amount
        : 0,
    currency: unit.property.currency as Currency,
    facilities: Array.from(
      new Set([
        ...(unit?.facilities?.map((item) =>
          item
            .split(" ")
            .map((word) =>
              word
                .split("-")
                .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
                .join("-")
            )
            .join(" ")
        ) || []),

        ...(unit?.en_suit ? ["En-Suite"] : []),
        ...(unit?.prepaid ? ["Prepaid"] : []),
        ...(unit?.pet_allowed ? ["Pet Allowed"] : []),
        ...(unit?.wardrobe ? ["Wardrobe"] : []),
      ])
    ),
    unit_name: unit.unit_name,
    unitSubtype: unit?.unit_sub_type,
    priceCondition: unit.negotiation === false ? "Net Price" : "Asking Price",
    secondTitle: unit.property.title,
    staffListing: unit?.staff_listing?.map(transformUnit),
    unitPreference: unit.unit_preference,
    agencyFee: unit.agency_fee,
    measurement: unit.measurement,
    videoLink: unit.property.video_link || "",
    discount: undefined,
    title: generateAddress(unit),
    address: unit.property
      ? `${unit.property.full_address || ""}, ${unit.property.city_area || ""
        }, ${unit.property.local_government || ""} ${unit.property.state
        }`.trim()
      : "",
    newType: unit.unit_type || undefined,
    per: unit.fee_period || undefined,
    category: unit.property?.category as "commercial",
    size: unit.total_area_sqm
      ? parseFloat(unit.total_area_sqm.replace(" sqm", "").replace(",", ""))
      : 0,
    propertyDesc: unit.property?.description || undefined,
    price: parseFloat(unit.total_package),
    priceType: "Total Package", // Assuming this is fixed
    quote: unit.fee_period as string, // Assuming this is fixed
    quotePrice: parseFloat(unit.fee_amount || ""),
    staff: extractMemberFromUnit(unit) as MemberType,
    strike: false,
    type: mapPropertyType("rental"), // Adjust this logic as needed
    reviews: [],
    serviceCharge: unit.service_charge
      ? parseFloat(unit.service_charge)
      : undefined,
    inspectionFee: unit.inspection_fee
      ? parseFloat(unit.inspection_fee)
      : undefined,
    refundableCautionFee: unit.caution_fee
      ? parseFloat(unit.caution_fee)
      : undefined,
    nonRefundableLegalFees: parseFloat(unit.legal_fee as string),
    otherFee: unit.other_charge ? parseFloat(unit.other_charge) : undefined,
    propertyBrief: [],
    bathroom: unit.bathroom ? parseInt(unit.bathroom, 10) : undefined,
    bedroom: unit.bedroom ? parseInt(unit.bedroom, 10) : undefined,
    toilet: unit.toilet,
    bookMarks: unit.total_bookmarks ?? undefined,
    addedDate: unit.created_at || undefined,
    lastUpdated: unit.updated_at || undefined,
    renew_fee_period: unit.renew_fee_period,
    renew_fee_amount: unit.renew_fee_amount,
    renew_service_charge: unit.renew_service_charge,
    renew_other_charge: unit.renew_other_charge,
    renew_total_package: unit.renew_total_package,
  };
};
function parseCoordinates(coordinate: string): [number, number] {
  if (!coordinate) return null as unknown as [number, number];
  const [latitude, longitude] = coordinate
    .split(",")
    .map((coord) => Number(coord.trim()));
  return [latitude, longitude];
}
export function mapPropertyType(
  input: "rental" | "sale" | "short let"
): "rent" | "sale" | "short let" {
  const typeMap: Record<string, "rent" | "sale" | "short let"> = {
    rental: "rent",
    sale: "sale",
    "short let": "short let",
  };

  return typeMap[input];
}
// taiwosalamandco.ourlisting.ng
// Hardcoded endpoint// Type for query parameters
interface FetchParams {
  [key: string]: string | number | boolean | undefined; // Flexible key-value pairs
  page?: number;
  limit?: number;
  // Add more specific params as needed
}

// Type for image object in the response
interface UnitImage {
  path: string;
  isDefault?: number;
  id?: number;
  mediaable_type?: string;
  mediaable_id?: number;
  created_at?: string;
  updated_at?: string;
}

// Type for the property object within a unit
interface UnitProperty {
  id: number;
  title: string;
  state?: string;
  currency?: Currency;
  local_government?: string;
  city_area?: string;
  company?: {
    directors: DirectorProfile[];
  };
  full_address: string;
  category: string;
  description?: string;
  property_type: string;
  video_link?: string | null;
  created_at?: string;
  updated_at?: string;
  coordinate?: string;
  directors?: DirectorProfile[];
}

type DirectorProfile = {
  id: number;
  name: string;
  tier: string;
  email: string;
  phone: string;
  picture: string;
  title: string;
  professional_title: string;
  company_id: number;
  company_name: string;
  company_logo: string;
};

type RawStaff = {
  id: number;
  name: string;
  tier?: string;
  email: string;
  phone: string | null;
  picture: string;
  estate_title: string | null;
  title: string;
};

type BranchManager = {
  id: number;
  branch_name: string;
  manager_id: number;
  tier: string;
  name: string;
  email: string;
  phone: string;
  picture: string;
  manager_title: string;
  manager_professional_title: string;
  company_id: number;
  company_name: string;
  company_logo: string;
};

// Type for a single unit in the response
export interface Unit {
  id: number;
  unit_name: string;
  unit_type?: string;
  number_of?: string;
  renew_agency_fee: string;
  renew_security_fee: string;
  total_bookmarks: number;
  negotiation: boolean;
  unit_sub_type?: string;
  unit_preference?: string;
  measurement?: string;
  management_fee: string;
  security_fee: string;
  bedroom?: string;
  bathroom?: string;
  toilet?: number;
  company_directors: DirectorProfile[];
  facilities?: string[] | null;
  en_suit: boolean;
  prepaid: boolean;
  pet_allowed: boolean;
  wardrobe: boolean;
  total_area_sqm?: string;
  fee_amount?: string;
  fee_period?: string;
  total_package: string;
  agency_fee: string;
  service_charge?: string;
  other_charge?: string;
  inspection_fee?: string;
  caution_fee?: string;
  legal_fee?: string;
  property_id: number;
  total_views?: number;
  total_unit: number;
  total_available_unit: number;
  bookmarks?: number;
  vat_amount: string;
  renew_vat_amount: string;
  created_at?: string;
  updated_at?: string;
  images: UnitImage[];
  property: UnitProperty;
  staff: RawStaff;
  branch_manager: BranchManager;
  staff_listing?: Unit[];
  renew_fee_period?: string;
  rent_vat: VAT[];
  renew_fee_amount?: string;
  renew_service_charge?: string;
  renew_other_charge?: string;
  renew_total_package?: string;
}

export function extractMemberFromUnit(unit: Unit): MemberType | null {
  const { staff, branch_manager, company_directors } = unit;
  if (
    staff &&
    staff.name &&
    staff.email &&
    staff.phone &&
    staff.picture &&
    staff.title
  ) {
    return {
      id: staff.id,
      name: `${staff.title} ${staff.name}`,
      title: staff.title,
      estateTitle: staff.estate_title || undefined,
      role: staff.estate_title as string,
      phone: staff.phone,
      email: staff.email,
      image: staff.picture || null,
      userId: undefined,
      companyId: undefined,
      is_verified: staff.tier == "2",
    };
  }

  // 2. Try from Branch Manager
  if (
    branch_manager &&
    branch_manager.name &&
    branch_manager.email &&
    branch_manager.phone &&
    branch_manager.picture &&
    branch_manager.manager_title
  ) {
    return {
      id: branch_manager.manager_id,
      name: `${branch_manager.manager_title} ${branch_manager.name}`,
      title: branch_manager.manager_title,
      estateTitle: undefined,
      role: branch_manager.manager_professional_title,
      phone: branch_manager.phone,
      is_verified: branch_manager.tier == "2",
      email: branch_manager.email,
      image: branch_manager.picture || null,
      userId: undefined,
      companyId: branch_manager.company_id,
      branch: branch_manager.branch_name,
    };
  }
  const director = company_directors?.[0];
  if (
    director &&
    director.name &&
    director.email &&
    director.phone &&
    director.picture &&
    director.professional_title
  ) {
    return {
      id: director.id,
      name: `${director.title} ${director.name}`,
      title: director.professional_title,
      is_verified: director.tier == "2",
      role: director.professional_title,
      phone: director.phone,
      email: director.email,
      image: director.picture || null,
      companyId: director.company_id,
    };
  }

  // If nothing matches, return null
  return null;
}

export type VAT = {
  id: number;
  vat_id: string;
  client_name: string | null;
  client_picture: string;
  description: string;
  amount: string;
  date: string;
};
// Type for the full API response
export interface UnitsResponse {
  units: Unit[];
  total_bookmarks: number;
  unit_type: string[];
  unit_sub_type: string[];
  unit_state: string[];
  unit_area: string[];
  unit_lga: string[];
  total_units: number;
  total_vacant: number;
  pagination?: {
    current_page: number;
    per_page: number;
    total: number;
    total_pages: number;
  };
}
// Axios function to fetch data from the API
export const fetchUnits = async (
  params: FetchParams = {},
  location?: string
): Promise<UnitsResponse> => {
  const isDemo = process.env.NEXT_PUBLIC_DEMO_MODE === "true";
  console.log(location);
  if (isDemo) {
    return {} as UnitsResponse;
  }
  try {
    const url = `${process.env.NEXT_PUBLIC_BASE_URL
      }api/v1/custom/company-listing/${location || clientDomain}`;
    if (!url) {
      throw new Error("API endpoint is not defined");
    }

    const response = await axios.get<UnitsResponse>(url, { params });
    if (!response?.data || !response.data.units) {
      throw new Error("Malformed response from server");
    }

    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error("Error fetching units:", error?.message || error);
    // Optionally, report to a monitoring service here

    // Gracefully return fallback or empty structure
    return {
      units: [],
      pagination: {
        current_page: 0,
        per_page: 0,
        total: 0,
        total_pages: 1,
      },
    } as unknown as UnitsResponse;
  }
};
export function generateRandomWord() {
  const letters = "abcdefghijklmnopqrstuvwxyz";
  let word = "";
  for (let i = 0; i < 3; i++) {
    word += letters.charAt(Math.floor(Math.random() * letters.length));
  }
  return word;
}
// React Query Hook with Pagination
const useFetchUnits = (
  initialParams: FetchParams = {},
  initialData?: UnitsResponse
) => {
  console.log({ initialParams }, isDemoMode);
  return useQuery({
    queryKey: ["companyUnits", JSON.stringify(initialParams)],
    queryFn: () => fetchUnits(initialParams),
    select: (data: UnitsResponse) => {
      const units = data.units.map(transformUnit); // Use the transformer here
      console.log(isDemoMode);
      if (isDemoMode) {
        const properties = [
          ...findPropertiesByType("rent"),
          ...findPropertiesByType("rent"),
          ...findPropertiesByType("rent"),
        ];
        console.log(properties);
        console.log("returning", {
          units: properties,
          pagination: {
            current_page: 10,
            per_page: 40,
            total: 40,
            total_pages: 1,
          },
        });
        return {
          units: properties,
          pagination: {
            current_page: 10,
            per_page: 40,
            total: 40,
            total_pages: 1,
          },
        };
      }
      return {
        units,
        pagination: data.pagination!,
      };
    },
    staleTime: 0, // 5 minutes stale time
    initialData: initialData,
  });
};
export default useFetchUnits;
